import {
  extractDurationFromNowMinus,
  formatWithTimeZone,
  getDateTimeFormat,
  isDurationString,
  isNowValue,
  isRelativeTimeValue,
  TimeFormat,
} from '@chronosphereio/core';
import { isValid, parse } from 'date-fns';

/**
 * Normalizes a time input value:
 * - Bare durations like "5m" become "now-5m"
 * - Other values are returned as-is
 */
export function normalizeTimeInput(value: string): string {
  const trimmed = value.trim();
  if (isDurationString(trimmed)) {
    return `now-${trimmed}`;
  }
  return trimmed;
}

/**
 * Formats a Date to a string using the specified time format and optional timezone.
 */
export function formatDateToString(date: Date | null, timeFormat: TimeFormat, timeZone?: string): string {
  if (!date || !isValid(date)) return '';
  const format = getDateTimeFormat({ timeFormat });
  return formatWithTimeZone(date, format, timeZone);
}

/**
 * Parses a date string to a Date object.
 * Returns undefined if the string cannot be parsed.
 * Only accepts dates in the expected format - does NOT use native Date parsing fallback
 * to avoid unexpected interpretations of invalid inputs. Only exported for testing purposes.
 */
export function parseDateString(value: string, timeFormat: TimeFormat): Date | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Reject strings that look like relative time expressions (even invalid ones)
  // to prevent them from being parsed as dates
  if (trimmed.toLowerCase().startsWith('now')) return undefined;

  // Reject bare numbers or number-like strings that could be misinterpreted
  if (/^\d+$/.test(trimmed)) return undefined;

  const format = getDateTimeFormat({ timeFormat });

  // Try parsing with the expected format only
  const parsed = parse(trimmed, format, new Date());
  if (isValid(parsed)) {
    // Additional sanity check: parsed date should be within a reasonable range
    // (not year 1 AD or year 9999)
    const year = parsed.getFullYear();
    if (year >= 1970 && year <= 2100) {
      return parsed;
    }
  }

  return undefined;
}

/**
 * Validates input for the "start" field.
 * Valid if: it's a valid Date string, or it's "now-x" (NOT plain "now"),
 * or it's a bare duration ("5m"), or it's empty.
 */
export function isValidStartTimeValue(value: string, timeFormat: TimeFormat): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;

  const maybeDuration = extractDurationFromNowMinus(trimmed);
  if (maybeDuration) {
    return isDurationString(maybeDuration);
  }

  if (isDurationString(trimmed)) {
    return true;
  }

  // Try to parse as a date
  const parsed = parseDateString(trimmed, timeFormat);
  return parsed !== undefined;
}

/**
 * Validates an end time value.
 * Valid if: it's a valid Date string, or it's exactly "now", or it's empty.
 * Does NOT accept "now-x" or bare durations
 */
export function isValidEndTimeValue(value: string, timeFormat: TimeFormat): boolean {
  const trimmed = value.trim();
  if (trimmed === '') return true;
  if (isNowValue(trimmed)) return true;

  // Try to parse as a date
  const parsed = parseDateString(trimmed, timeFormat);
  return parsed !== undefined;
}

/**
 * Converts a string to a Date if possible, otherwise returns undefined.
 * Returns undefined for relative time expressions like "now" or "now-5m".
 */
export function tryParseDateString(value: string, timeFormat: TimeFormat): Date | undefined {
  const trimmed = value.trim();
  if (trimmed === '' || isRelativeTimeValue(trimmed)) return undefined;
  return parseDateString(trimmed, timeFormat);
}
